<template>
    <div>
        <v-alert color="info" text>
            {{ $t('Tehosteväriä käytetään sovelluksen painikkeissa, otsikoissa ja korostuksissa.') }}
        </v-alert>

        <div class="d-flex flex-wrap" style="gap: 24px;">
            <v-color-picker
                v-model="selectedColor"
                mode="hexa"
                hide-mode-switch
                flat
            />

            <div class="flex-grow-1" style="min-width: 220px; max-width: 320px;">
                <div class="text-subtitle-2 mb-2">{{ $t("Esikatselu") }}</div>
                <div class="preview-card" :style="{ borderColor: selectedColor }">
                    <div class="preview-header" :style="{ backgroundColor: selectedColor }">
                        {{ tenantName || $t("Esimerkki") }}
                    </div>
                    <div class="pa-3">
                        <v-btn :color="selectedColor" dark small class="mb-2">
                            {{ $t("Painike") }}
                        </v-btn>
                        <div class="text-caption" :style="{ color: selectedColor }">
                            {{ selectedColor }}
                        </div>
                    </div>
                </div>
            </div>
        </div>

        <div class="mt-4">
            <v-btn
                color="primary"
                @click="saveColor"
                :loading="isSubmitting"
                :disabled="!hasChanges"
            >
                {{ $t("Tallenna") }}
            </v-btn>
            <v-btn
                outlined
                class="ml-2"
                @click="reset"
                :disabled="isSubmitting || !hasChanges"
            >
                {{ $t("Peruuta") }}
            </v-btn>
        </div>

        <v-alert v-if="errorMessage" type="error" text class="mt-4" dismissible @input="errorMessage = ''">
            {{ errorMessage }}
        </v-alert>
    </div>
</template>

<script>
import { ref, computed, watch } from "@vue/composition-api";
import adminApi from "@/api/admin";
import api from "@/api";
import useAuth from "@/useAuth";
import { i18n } from "@/plugins/i18n";

const DEFAULT_COLOR = "#1976D2";

const normalizeHex = (value) => {
    if (!value) return DEFAULT_COLOR;
    let v = String(value).trim();
    if (!v.startsWith("#")) v = "#" + v;
    return v.toUpperCase();
};

export default {
    setup() {
        const { tenant, setTenant } = useAuth();

        const initialColor = normalizeHex(tenant.value && tenant.value.color);
        const selectedColor = ref(initialColor);
        const savedColor = ref(initialColor);
        const isSubmitting = ref(false);
        const errorMessage = ref("");

        watch(
            () => tenant.value && tenant.value.color,
            (val) => {
                const normalized = normalizeHex(val);
                savedColor.value = normalized;
                selectedColor.value = normalized;
            }
        );

        const tenantName = computed(() => (tenant.value && tenant.value.name) || "");

        const hasChanges = computed(() => {
            const a = (selectedColor.value || "").substring(0, 7).toUpperCase();
            const b = (savedColor.value || "").substring(0, 7).toUpperCase();
            return a !== b;
        });

        const reset = () => {
            selectedColor.value = savedColor.value;
            errorMessage.value = "";
        };

        const saveColor = async () => {
            isSubmitting.value = true;
            errorMessage.value = "";
            try {
                const hex = (selectedColor.value || "").substring(0, 7);
                const res = await adminApi.updateTenantColor(hex);
                if (res.ok) {
                    const tenantRes = await api.getTenant();
                    if (tenantRes.ok) {
                        setTenant(tenantRes.data);
                    }
                } else {
                    const detail =
                        (res.data && (res.data.detail || (res.data.color && res.data.color[0]))) ||
                        i18n.t("Värin tallennus epäonnistui.");
                    errorMessage.value = detail;
                }
            } catch (e) {
                errorMessage.value = i18n.t("Värin tallennus epäonnistui.");
            } finally {
                isSubmitting.value = false;
            }
        };

        return {
            selectedColor,
            isSubmitting,
            errorMessage,
            hasChanges,
            tenantName,
            saveColor,
            reset,
        };
    },
};
</script>

<style scoped>
.preview-card {
    border: 2px solid;
    border-radius: 4px;
    overflow: hidden;
}

.preview-header {
    color: #fff;
    padding: 12px 16px;
    font-weight: 500;
}
</style>
